import { createContext, useContext, useState, type ReactNode } from 'react'

import type {
  StaticPublishScope,
  StaticSite,
  StaticSiteDeployment,
  StaticSiteReceipt,
  StaticSiteVersion,
} from '../../app/static-sites'
import { livePublicUrl, primaryStaticSite, STATIC_PUBLISH_FILE_ACCEPT } from '../../app/static-sites'
import {
  useFastPublishStaticSiteFor,
  useFastRollbackStaticSiteFor,
  useProject,
  useProjectStaticSiteSummary,
  useRenameProject,
  useStaticSiteDeploymentsFor,
  useStaticSiteVersionsFor,
  useStaticSitesFor,
} from '../../app/queries'
import { bytes, hhmm } from '../../app/format'
import { Button } from '../../components/button'
import { Card, CardTitle } from '../../components/card'
import { cn } from '../../components/cn'
import { Tag, type TagTone } from '../../components/tag'
import { T } from '../../copy/text'
import { t, type CopyId } from '../../copy'
import { pj } from '../../copy/pjshell'
import { PJ } from '../../copy/pjshell-text'
import type { PjshellId } from '../../copy/pjshell'
import {
  DataCell,
  DataRow,
  DataTable,
  StatusPill,
  Unverified,
  luluButtonClass,
} from './modules/ui'

const Surface = createContext<'paper' | 'lulu'>('paper')

const SITE_GRID =
  'minmax(12rem,1.4fr) minmax(5rem,1fr) 72px 120px 96px 80px minmax(10rem,0.9fr)'

export function StaticSitesSurface({
  value,
  children,
}: {
  value: 'paper' | 'lulu'
  children: ReactNode
}) {
  return <Surface.Provider value={value}>{children}</Surface.Provider>
}

function useLulu(): boolean {
  return useContext(Surface) === 'lulu'
}

function resolveScope(scope?: StaticPublishScope, projectId?: string): StaticPublishScope {
  return scope ?? { kind: 'project', projectId: projectId as string }
}

/**
 * 项目详情页那张「发个网页」卡（HTML 快速发布）。
 *
 * 工作区本身按 scope 复用：项目走 `projectId`，一级「发布」走账户公共站。
 * **拖进来就是发出去。** 登录用户走 `/fast-publish` 与 `/fast-rollback`，
 * 不要批准码、理由或二次验证。旧的 `/publish` 还在，这张卡不再调用。
 */
export function StaticSitesCard({
  projectId,
  surface = 'paper',
}: {
  projectId: string
  surface?: 'paper' | 'lulu'
}) {
  return (
    <StaticPublishWorkspace
      scope={{ kind: 'project', projectId }}
      surface={surface}
      showTitle={surface !== 'lulu'}
    />
  )
}

export function StaticPublishWorkspace({
  scope,
  surface = 'paper',
  showTitle,
  aside,
}: {
  scope: StaticPublishScope
  surface?: 'paper' | 'lulu'
  showTitle?: boolean
  aside?: ReactNode
}) {
  const sites = useStaticSitesFor(scope)
  const [openSiteId, setOpenSiteId] = useState<string | null>(null)
  const lulu = surface === 'lulu'
  const title = showTitle ?? !lulu
  const showProjectName = scope.kind === 'project' && !lulu
  const showNewSite = lulu || scope.kind === 'account'
  const primary = scope.kind === 'project' ? primaryStaticSite(sites.data) : undefined
  const testId = scope.kind === 'project' ? 'project-static-sites' : 'account-static-sites'
  const loadingId = scope.kind === 'account' ? 'static_site.account.loading' : 'static_site.loading'
  const emptyId = scope.kind === 'account' ? 'static_site.account.empty' : 'static_site.empty'

  const drop = (
    <DropZone
      scope={scope}
      siteId={primary?.id}
      testId="static-site-drop"
      labelId="static_site.drop.label"
      disabled={sites.isPending}
    />
  )

  const list = sites.isPending ? (
    <div className={cn('text-npc-caption', lulu ? 'text-npc-lulu-t3' : 'text-npc-ink-soft')}>
      <T id={loadingId} />
    </div>
  ) : sites.error ? (
    <Problem message={sites.error.message} />
  ) : sites.data && sites.data.length > 0 ? (
    lulu ? (
      <SitesTable scope={scope} sites={sites.data} />
    ) : (
      <div className="flex flex-col gap-npc-gap">
        {sites.data.map((site) => (
          <SiteRow
            key={site.id}
            scope={scope}
            site={site}
            open={openSiteId === site.id}
            onToggle={() => setOpenSiteId(openSiteId === site.id ? null : site.id)}
          />
        ))}
      </div>
    )
  ) : lulu ? null : (
    <div className={cn('text-npc-caption', 'text-npc-ink-soft')}>
      <T id={emptyId} />
    </div>
  )

  const body = lulu ? (
    <div data-testid={testId} className="min-w-0 max-w-full">
      <div className="mb-npc-grid grid grid-cols-1 gap-npc-gap-sm min-[601px]:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-npc-gap-sm">
          {drop}
          {showNewSite ? (
            <div className="flex flex-wrap items-center gap-npc-gap-sm">
              <DropZone
                scope={scope}
                newSite
                testId="static-site-new"
                layout="button"
                surface="lulu"
              />
            </div>
          ) : null}
          <ProjectDropHint
            scope={scope}
            primary={primary}
            emptyPending={sites.isPending}
            hasSites={(sites.data?.length ?? 0) > 0}
          />
        </div>
        {aside}
      </div>
      {list}
    </div>
  ) : (
    <>
      {title ? (
        <>
          <CardTitle className="mb-[3px]">
            <T id="static_site.section.title" />
          </CardTitle>
          <div className="mb-npc-gap-lg text-npc-caption-sm leading-[1.6] text-npc-ink-soft">
            <T id="static_site.section.hint" />
          </div>
        </>
      ) : null}
      {showProjectName && scope.kind === 'project' ? (
        <ProjectNameField projectId={scope.projectId} />
      ) : null}
      {list}
      {showNewSite ? (
        <div className="mt-npc-gap-sm flex flex-wrap items-center gap-npc-gap-sm">
          <DropZone scope={scope} newSite testId="static-site-new" layout="button" />
        </div>
      ) : null}
      {drop}
    </>
  )

  return (
    <Surface.Provider value={surface}>
      {lulu ? (
        body
      ) : (
        <Card data-testid={testId}>{body}</Card>
      )}
    </Surface.Provider>
  )
}

function ProjectDropHint({
  scope,
  primary,
  emptyPending,
  hasSites,
}: {
  scope: StaticPublishScope
  primary?: StaticSite
  emptyPending: boolean
  hasSites: boolean
}) {
  const lulu = useLulu()
  if (!lulu || emptyPending) return null
  if (scope.kind === 'account') {
    if (hasSites) return null
    return (
      <p className="mb-0 mt-0 text-npc-meta text-npc-lulu-t4">
        <T id="static_site.account.empty" />
      </p>
    )
  }
  return <PrimaryNowHint projectId={scope.projectId} primary={primary} />
}

function PrimaryNowHint({ projectId, primary }: { projectId: string; primary?: StaticSite }) {
  const summary = useProjectStaticSiteSummary(projectId)
  const deployments = useStaticSiteDeploymentsFor(
    { kind: 'project', projectId },
    primary?.id ?? null,
  )
  const version = summary.data?.live_version_no ?? deployments.data?.[0]?.version_no ?? null
  return (
    <p className="mb-0 mt-0 text-npc-meta text-npc-lulu-t4">
      {primary && version != null ? (
        <PJ
          id="pjshell.release.primary_now"
          vars={{ slug: primary.slug, version: String(version) }}
        />
      ) : primary ? (
        <Unverified />
      ) : (
        <PJ id="pjshell.release.drop.empty" />
      )}
    </p>
  )
}

/**
 * 项目显示名。第一次发布时，网址里那一段就是从这个名字来的。
 *
 * 改名**只改元数据**：已经发出去的域名不迁移、不重定向（裁决 2026-08-23）。
 */
function ProjectNameField({ projectId }: { projectId: string }) {
  const project = useProject(projectId)
  const rename = useRenameProject(projectId)
  const [draft, setDraft] = useState<string | null>(null)
  const lulu = useLulu()

  const current = project.data?.name ?? ''
  const value = draft ?? current
  const dirty = draft !== null && draft.trim() !== '' && draft !== current

  return (
    <section className="mb-npc-gap-lg">
      <SectionLabel id="static_site.name.label" />
      <div className="flex items-center gap-npc-gap-xs">
        <input
          type="text"
          data-testid="static-site-project-name"
          aria-label={t('static_site.name.label')}
          value={value}
          disabled={project.isPending || rename.isPending}
          onChange={(event) => setDraft(event.target.value)}
          className={cn(
            'min-w-0 flex-1 rounded-npc-inset border px-npc-gap-xs py-[6px] text-npc-caption',
            lulu
              ? 'border-npc-lulu-line-2 bg-npc-lulu-bg-2 text-npc-lulu-t1'
              : 'border-npc-border bg-npc-panel text-npc-ink',
          )}
        />
        <Button
          size="sm"
          data-testid="static-site-project-name-save"
          disabled={!dirty || rename.isPending}
          onClick={() => {
            if (!dirty) return
            rename.mutate(draft!.trim(), { onSuccess: () => setDraft(null) })
          }}
        >
          <T id="static_site.name.save" />
        </Button>
      </div>
      <div className="mt-npc-gap-xs text-npc-caption-sm leading-[1.6] text-npc-ink-soft">
        <T id="static_site.name.url_is_fixed" />
      </div>
      {rename.error ? <Problem message={rename.error.message} /> : null}
      {rename.isSuccess && draft === null ? (
        <div className="mt-npc-gap-xs text-npc-caption-sm text-npc-ink-soft">
          <T id="static_site.name.saved" />
        </div>
      ) : null}
    </section>
  )
}

const statusCopy: Record<StaticSite['status'], CopyId> = {
  active: 'static_site.status.active',
  suspended: 'static_site.status.suspended',
  offlined: 'static_site.status.offlined',
}

const statusTone: Record<StaticSite['status'], TagTone> = {
  active: 'ok',
  suspended: 'warn',
  offlined: 'neutral',
}

export function deploymentStatusCopy(deployment: StaticSiteDeployment): PjshellId {
  if (deployment.status === 'active') return 'pjshell.release.live'
  if (deployment.status === 'failed') return 'pjshell.release.failed'
  if (deployment.status === 'pending') {
    return deployment.receipt?.health.state === 'pending'
      ? 'pjshell.release.verifying'
      : 'pjshell.release.publishing'
  }
  return 'pjshell.release.checking'
}

function siteRowStatusCopy(
  site: StaticSite,
  deployment: StaticSiteDeployment | undefined,
): PjshellId | 'unverified' {
  if (deployment?.status === 'pending' && deployment.receipt?.health.state === 'pending') {
    return 'pjshell.release.verifying.public'
  }
  if (
    site.status === 'active' &&
    deployment?.status === 'active' &&
    (deployment.receipt?.health.state === 'passed' || Boolean(deployment.verified_at))
  ) {
    return 'pjshell.release.live'
  }
  if (deployment) return deploymentStatusCopy(deployment)
  return 'unverified'
}

function SiteRow({
  scope,
  site,
  open,
  onToggle,
}: {
  scope: StaticPublishScope
  site: StaticSite
  open: boolean
  onToggle: () => void
}) {
  const deployments = useStaticSiteDeploymentsFor(scope, site.id)
  const url = livePublicUrl(site.slug, deployments.data)
  const lulu = useLulu()

  return (
    <div
      className={cn(
        'rounded-npc-inset border px-npc-grid-sm py-npc-row-y',
        lulu ? 'border-npc-lulu-line-2' : 'border-npc-border-soft',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-npc-gap-sm">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={cn(
            'min-w-0 flex-1 text-left text-npc-body-sm font-medium',
            lulu ? 'text-npc-lulu-t1 hover:text-npc-lulu-t2' : 'text-npc-ink hover:text-npc-ink-strong',
          )}
        >
          {site.title.trim() || site.slug}
        </button>
        <Tag tone={statusTone[site.status]}>
          <T id={statusCopy[site.status]} />
        </Tag>
      </div>

      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid="static-site-url"
          className={cn(
            'mt-npc-gap-xs block truncate font-npc-mono text-npc-meta',
            lulu ? 'text-npc-lulu-t3 hover:text-npc-lulu-t1' : 'text-npc-ink-soft hover:text-npc-ink',
          )}
        >
          {url}
        </a>
      ) : null}

      {open ? <SiteDetail scope={scope} site={site} /> : null}
    </div>
  )
}

function SiteDetail({ scope, site }: { scope: StaticPublishScope; site: StaticSite }) {
  const versions = useStaticSiteVersionsFor(scope, site.id)
  const deployments = useStaticSiteDeploymentsFor(scope, site.id)

  return (
    <div className="mt-npc-gap-lg flex flex-col gap-npc-gap-lg border-t border-npc-border-softer pt-npc-gap-lg">
      <Live deployments={deployments.data ?? []} versions={versions.data ?? []} />
      <Versions scope={scope} site={site} versions={versions.data ?? []} />
    </div>
  )
}

function SitesTable({ scope, sites }: { scope: StaticPublishScope; sites: StaticSite[] }) {
  if (sites.length === 0) return null
  return (
    <div className="max-[600px]:[&_.grid]:!flex max-[600px]:[&_.grid]:!flex-col max-[600px]:[&_.tracking-npc-label.font-bold]:hidden">
      <DataTable
        grid={SITE_GRID}
        hint={<PJ id="pjshell.release.sites" />}
        columns={[
          <PJ key="url" id="pjshell.release.col.url" />,
          <PJ key="name" id="pjshell.release.col.name" />,
          <PJ key="ver" id="pjshell.release.col.version" />,
          <PJ key="st" id="pjshell.release.col.status" />,
          <PJ key="last" id="pjshell.release.col.last" />,
          <PJ key="size" id="pjshell.release.col.size" />,
          <PJ key="act" id="pjshell.release.col.actions" />,
        ]}
      >
        {sites.map((site) => (
          <SiteTableRow key={site.id} scope={scope} site={site} />
        ))}
      </DataTable>
    </div>
  )
}

function SiteTableRow({ scope, site }: { scope: StaticPublishScope; site: StaticSite }) {
  const versions = useStaticSiteVersionsFor(scope, site.id)
  const deployments = useStaticSiteDeploymentsFor(scope, site.id)
  const latest = deployments.data?.[0]
  const url = livePublicUrl(site.slug, deployments.data)
  const versionNo =
    latest?.version_no ?? versions.data?.find((item) => item.id === latest?.version_id)?.version_no ?? null
  const sizeSource =
    versions.data?.find((item) => item.id === latest?.version_id) ?? versions.data?.[0]
  const status = siteRowStatusCopy(site, latest)
  const lastAt = latest?.verified_at ?? latest?.created_at

  return (
    <>
      <DataRow grid={SITE_GRID}>
        <DataCell mono>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              data-testid="static-site-url"
              className="truncate text-npc-lulu-t3 hover:text-npc-lulu-t1"
            >
              {url}
            </a>
          ) : deployments.isPending ? null : (
            <Unverified />
          )}
        </DataCell>
        <DataCell>{site.title.trim() || site.slug}</DataCell>
        <DataCell>{versionNo != null ? `v${versionNo}` : <Unverified />}</DataCell>
        <DataCell>
          {status === 'unverified' ? (
            <Unverified />
          ) : (
            <StatusPill
              tone={
                status === 'pjshell.release.live'
                  ? 'ok'
                  : status === 'pjshell.release.failed'
                    ? 'bad'
                    : 'warn'
              }
            >
              <PJ id={status} />
            </StatusPill>
          )}
        </DataCell>
        <DataCell mono>{lastAt ? hhmm(lastAt) : <Unverified />}</DataCell>
        <DataCell mono>{sizeSource ? bytes(sizeSource.byte_size) : <Unverified />}</DataCell>
        <DataCell>
          <div className="flex flex-wrap items-center gap-npc-gap-xs">
            {url ? (
              <a href={url} target="_blank" rel="noreferrer" className={luluButtonClass}>
                <PJ id="pjshell.release.open" />
              </a>
            ) : null}
            <DropZone
              scope={scope}
              siteId={site.id}
              testId={`static-site-replace-${site.id}`}
              layout="button"
              surface="lulu"
            />
          </div>
        </DataCell>
      </DataRow>
      {versions.data && versions.data.length > 0 ? (
        <div className="border-t border-npc-lulu-line-1 px-npc-grid-sm py-npc-gap-sm">
          <details open>
            <summary className="cursor-pointer text-npc-meta text-npc-lulu-t3">
              <T id="static_site.versions.title" />
            </summary>
            <div className="mt-npc-gap-sm">
              <Versions hideTitle scope={scope} site={site} versions={versions.data} />
            </div>
          </details>
        </div>
      ) : null}
    </>
  )
}

/**
 * 「现在大家看到的」。
 *
 * 取的是部署历史里最新那条，四档各说各的——**它们要求的下一个动作不一样**：
 * pending 要等、active 可以去看、failed 要重发（而旧版还在服务）、
 * superseded 什么都不用做。合成一句「发布状态」等于把这四个决定推回给用户。
 */
function Live({
  deployments,
  versions,
}: {
  deployments: StaticSiteDeployment[]
  versions: StaticSiteVersion[]
}) {
  const latest = deployments[0]
  const activeNo = latest ? versionNoOf(versions, latest.version_id) : null

  return (
    <section>
      <SectionLabel id="static_site.live.title" />
      {!latest ? (
        <div className="text-npc-caption text-npc-ink-soft">
          <T id="static_site.live.none" />
        </div>
      ) : latest.status === 'active' ? (
        activeNo === null ? null : (
          <>
            <div className="text-npc-caption text-npc-green-deep">
              <T
                id="static_site.live.active"
                vars={{ version_no: activeNo, when: hhmm(latest.verified_at ?? latest.created_at) }}
              />
            </div>
            <Receipt deployment={latest} versions={versions} />
          </>
        )
      ) : latest.status === 'pending' ? (
        <div className="text-npc-caption text-npc-amber-ink">
          <T id="static_site.live.pending" />
        </div>
      ) : latest.status === 'failed' ? (
        <div className="text-npc-caption text-npc-red-ink">
          <T id="static_site.live.failed" />
        </div>
      ) : (
        <div className="text-npc-caption text-npc-ink-soft">
          <T id="static_site.live.superseded" />
        </div>
      )}
    </section>
  )
}

function Receipt({
  deployment,
  versions,
}: {
  deployment: StaticSiteDeployment
  versions: StaticSiteVersion[]
}) {
  const receipt = deployment.receipt
  if (!receipt) return null

  const rollbackNo = receipt.rollback_target.exists
    ? versionNoOf(versions, receipt.rollback_target.version_id ?? '')
    : null

  return (
    <details className="mt-npc-gap-xs" data-testid="static-site-receipt">
      <summary className="cursor-pointer text-npc-meta text-npc-ink-softer">
        <T id="static_site.receipt.toggle" />
      </summary>
      <div className="mt-npc-gap-xs flex flex-col gap-npc-gap-xs text-npc-meta text-npc-ink-soft">
        <div data-testid="static-site-receipt-artifact">
          <T
            id="static_site.receipt.artifact"
            vars={{ digest: receipt.artifact_digest.slice(0, 12) }}
          />
        </div>
        <div data-testid="static-site-receipt-health">
          <HealthLine state={receipt.health.state} />
        </div>
        <div data-testid="static-site-receipt-rollback">
          {receipt.rollback_target.exists && rollbackNo !== null ? (
            <T id="static_site.receipt.rollback_to" vars={{ version_no: rollbackNo }} />
          ) : (
            <T id="static_site.receipt.rollback_none" />
          )}
        </div>
      </div>
    </details>
  )
}

function HealthLine({ state }: { state: StaticSiteReceipt['health']['state'] }) {
  if (state === 'passed') return <T id="static_site.receipt.health_passed" />
  if (state === 'failed') return <T id="static_site.receipt.health_failed" />
  if (state === 'pending') return <T id="static_site.receipt.health_pending" />
  return <T id="static_site.receipt.health_unknown" />
}

function versionNoOf(versions: StaticSiteVersion[], versionId: string): number | null {
  return versions.find((v) => v.id === versionId)?.version_no ?? null
}

export function DropZone({
  scope,
  projectId,
  siteId,
  newSite = false,
  testId,
  labelId = 'static_site.drop.label',
  layout = 'zone',
  surface,
  disabled = false,
}: {
  scope?: StaticPublishScope
  projectId?: string
  siteId?: string
  newSite?: boolean
  testId: string
  labelId?: 'static_site.drop.label'
  layout?: 'zone' | 'button'
  surface?: 'paper' | 'lulu'
  disabled?: boolean
}) {
  const resolved = resolveScope(scope, projectId)
  const publish = useFastPublishStaticSiteFor(resolved)
  const [over, setOver] = useState(false)
  const ctx = useContext(Surface)
  const lulu = (surface ?? ctx) === 'lulu'
  const blocked = disabled || publish.isPending

  function take(files: FileList | null) {
    const file = files?.[0]
    if (!file || blocked) return
    if (newSite) publish.mutate({ file, newSite: true })
    else publish.mutate({ file, ...(siteId ? { siteId } : {}) })
  }

  const ariaLabel = lulu
    ? newSite
      ? pj('pjshell.release.drop.new_site')
      : layout === 'button'
        ? pj('pjshell.release.replace')
        : pj('pjshell.release.drop.choose')
    : t(labelId)

  const input = (
    <input
      type="file"
      accept={STATIC_PUBLISH_FILE_ACCEPT}
      className="sr-only"
      aria-label={ariaLabel}
      data-testid={`${testId}-input`}
      disabled={blocked}
      onChange={(event) => {
        take(event.target.files)
        event.target.value = ''
      }}
    />
  )

  const feedback = (
    <>
      {publish.isPending ? (
        <div className="mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          <T id="static_site.drop.busy" />
        </div>
      ) : null}
      {publish.error ? <Problem message={publish.error.message} /> : null}
      {publish.data ? (
        <div className="mt-npc-gap-xs text-npc-caption text-npc-green-deep">
          <T id="static_site.drop.done" />
        </div>
      ) : null}
    </>
  )

  if (layout === 'button') {
    return (
      <span className="inline-flex flex-col">
        <label className={cn('cursor-pointer', lulu ? luluButtonClass : undefined)}>
          {input}
          {newSite ? <PJ id="pjshell.release.drop.new_site" /> : <PJ id="pjshell.release.replace" />}
        </label>
        {feedback}
      </span>
    )
  }

  return (
    <section
      className={cn(
        lulu ? undefined : 'mt-npc-grid-lg border-t pt-npc-grid-sm',
        lulu ? undefined : 'border-npc-border-softer',
      )}
    >
      {lulu ? null : <SectionLabel id={labelId} />}
      <label
        data-testid={testId}
        onDragOver={(event) => {
          event.preventDefault()
          setOver(true)
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault()
          setOver(false)
          take(event.dataTransfer.files)
        }}
        className={cn(
          'block cursor-pointer rounded-npc-inset border border-dashed',
          lulu
            ? 'px-npc-grid-sm py-npc-card text-npc-body-sm text-npc-lulu-t3'
            : 'px-npc-grid-sm py-npc-row-y text-npc-caption text-npc-ink-soft',
          over
            ? lulu
              ? 'border-npc-lulu-line-3 bg-npc-lulu-bg-3'
              : 'border-npc-green bg-npc-green-bg'
            : lulu
              ? 'border-npc-lulu-line-3'
              : 'border-npc-border',
        )}
      >
        {input}
        {lulu ? (
          <span className="flex flex-col items-start gap-npc-gap-sm">
            <span className="text-npc-body-sm font-medium text-npc-lulu-t1">
              <PJ id="pjshell.release.drop" />
            </span>
            <span className={luluButtonClass}>
              <PJ id="pjshell.release.drop.choose" />
            </span>
          </span>
        ) : (
          <T id="static_site.drop.hint" />
        )}
      </label>
      {feedback}
      {siteId || newSite ? null : (
        <div className="mt-npc-gap-sm text-npc-meta leading-[1.5] text-npc-ink-softer">
          <T id="static_site.url_hint" />
        </div>
      )}
    </section>
  )
}

export function Versions({
  scope,
  projectId,
  site,
  versions,
  hideTitle = false,
}: {
  scope?: StaticPublishScope
  projectId?: string
  site: StaticSite
  versions: StaticSiteVersion[]
  hideTitle?: boolean
}) {
  const resolved = resolveScope(scope, projectId)
  const rollback = useFastRollbackStaticSiteFor(resolved, site.id)

  return (
    <section>
      {hideTitle ? null : <SectionLabel id="static_site.versions.title" />}
      {versions.length === 0 ? (
        <div className="text-npc-caption text-npc-ink-soft">
          <T id="static_site.versions.empty" />
        </div>
      ) : (
        <div className="flex flex-col gap-npc-gap-xs">
          {versions.map((version) => (
            <div
              key={version.id}
              className="rounded-npc-inset bg-npc-surface px-npc-grid-sm py-npc-gap-xs"
            >
              <div className="flex flex-wrap items-center justify-between gap-npc-gap-sm">
                <span className="text-npc-caption text-npc-ink">
                  <T
                    id="static_site.versions.row"
                    vars={{ version_no: version.version_no, size: bytes(version.byte_size) }}
                  />
                </span>
                <Button
                  size="sm"
                  data-testid={`static-site-rollback-${version.id}`}
                  disabled={rollback.isPending}
                  onClick={() => rollback.mutate(version.id)}
                >
                  <T id="static_site.rollback.action" />
                </Button>
              </div>
              {rollback.isPending && rollback.variables === version.id ? (
                <div className="mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
                  <T id="static_site.rollback.busy" />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {rollback.error ? <Problem message={rollback.error.message} /> : null}
    </section>
  )
}

function SectionLabel({ id }: { id: CopyId }) {
  const lulu = useLulu()
  return (
    <div className={cn('mb-npc-gap-xs text-npc-body-sm font-medium', lulu ? 'text-npc-lulu-t1' : 'text-npc-ink')}>
      <T id={id} />
    </div>
  )
}

function Problem({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="mt-npc-gap-xs rounded-npc-inset border border-npc-red-border bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink"
    >
      {message}
    </div>
  )
}
